/**
 * compileTheme — the ONE renderer. Studio canvas, live community view
 * (CommunityThemeScope) and marketplace thumbnails all consume this output, so
 * the preview is guaranteed to equal production. It deliberately reuses the
 * same class helpers the canvas always used (studioModel) — one source of
 * truth for every visual decision.
 */
import { getTheme } from '@/features/theme/themes';
import { themeAssetUrl } from '@/features/theme/themeAssets';
import {
  bgTintOpacity,
  cardClass,
  dimOverlay,
  fontClass,
  radiusClass,
  railWidthPx,
  rowPadClass,
  type MessageStyle,
  type NameStyle,
} from '../studioModel';
import type { BannerDesign } from './bannerLayers';
import type { ThemeDoc } from './doc';

export interface CompiledTheme {
  /** CSS custom properties to set on the themed container. */
  vars: Record<string, string>;
  /** Tailwind class fragments (compose with cn()). */
  fontClass: string;
  cardClass: string;
  radiusClass: string;
  rowPadClass: string;
  /** Resolved raw values for JS-driven styling. */
  accent: string;
  backgroundImage: string | null;
  dimOverlay: string;
  tintOpacity: number;
  vignette: boolean;
  glowShadow: string | undefined;
  animated: boolean;
  railWidthPx: string;
  messageStyle: MessageStyle;
  nameStyle: NameStyle;
  joinLabel: string;
  bannerUrl: string | null;
  bannerFocal: { x: number; y: number };
  /** Editable layered banner — themed surfaces render this live when present. */
  bannerDesign: BannerDesign | null;
}

/** Resolve the doc's background reference to a loadable image URL (or null). */
export function docBackgroundUrl(doc: ThemeDoc): string | null {
  const { kind, ref } = doc.background;
  if (kind === 'asset') return ref;
  if (kind === 'catalog') return themeAssetUrl(getTheme(ref ?? '')?.file);
  return null;
}

export function compileTheme(doc: ThemeDoc): CompiledTheme {
  const accent = doc.accent;
  const backgroundImage = docBackgroundUrl(doc);
  const overlay = dimOverlay(doc.background.dim);
  const glowShadow = doc.effects.glow ? `0 0 14px ${accent}88` : undefined;

  return {
    vars: {
      '--t-accent': accent,
      '--winr-accent': accent,
      '--t-bg-image': backgroundImage ? `${overlay}, url(${backgroundImage})` : 'none',
      '--t-glow': glowShadow ?? 'none',
    },
    fontClass: fontClass(doc.font),
    cardClass: cardClass(doc.surface.material),
    radiusClass: radiusClass(doc.surface.corners),
    rowPadClass: rowPadClass(doc.surface.density),
    accent,
    backgroundImage,
    dimOverlay: overlay,
    tintOpacity: bgTintOpacity(doc.background.tint),
    vignette: doc.background.vignette,
    glowShadow,
    animated: doc.effects.animated,
    railWidthPx: railWidthPx(doc.layout.railWidth),
    messageStyle: doc.layout.messageStyle,
    nameStyle: doc.identity.nameStyle,
    joinLabel: doc.identity.joinLabel,
    bannerUrl: doc.identity.banner.url,
    bannerFocal: doc.identity.banner.focal,
    bannerDesign: doc.identity.banner.design?.layers.length ? doc.identity.banner.design : null,
  };
}
